import { formatDate } from '@angular/common';
import { Component, EventEmitter, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@ngneat/reactive-forms';
import { forkJoin, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CopyStaffFromOtherDayComponent } from 'src/app/check-hours/copy-staff-from-other-day/copy-staff-from-other-day.component';
import { MoveRealisationsToPerformerDialogComponent } from 'src/app/check-hours/move-realisations-to-performer-dialog/move-realisations-to-performer-dialog.component';
import { Entity } from 'src/app/classes/entity.class';
import { Planning } from 'src/app/classes/planning.class';
import { Project } from 'src/app/classes/project.class';
import { Realisation, RealisationHourtype } from 'src/app/classes/realisation';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import type { RealisationGroup } from 'src/app/components/hour-control/hour-control.types';
import { SelectProjectDialogComponent } from 'src/app/components/select-project-dialog/select-project-dialog.component';
import { ApiResponse } from 'src/app/services/api/api.service';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Features } from 'src/app/services/auth/features';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { EntitiesService } from 'src/app/services/entities/entities.service';
import { RealisationCount, RealisationService } from 'src/app/services/realisation.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { environment } from 'src/environments/environment';
import { ProjectEditDialogComponent } from '../projects/project-edit-dialog/project-edit-dialog.component';
import { AddRealisationDialogComponent } from './add-realisation-dialog/add-realisation-dialog.component';
import { CopyRealisationFromOtherDayComponent } from './copy-realisation-from-other-day/copy-realisation-from-other-day.component';
import { EditRealisationProjectDialogComponent } from './edit-realisation-project-dialog/edit-realisation-project-dialog.component';
import { NotEditablePipe } from './not-editable.pipe';

@Component({
    selector: 'app-check-hours',
    templateUrl: './check-hours.component.html',
    styleUrls: ['./check-hours.component.scss'],
    standalone: false,
})
export class CheckHoursComponent extends CodaltComponent implements OnInit {
    readonly RealisationHourtype = RealisationHourtype;
    readonly environment = environment;
    readonly canApproveHours = AuthorisationService.canApproveHours();

    openPreviousDays: RealisationCount[];
    realisations: Realisation[] = [];
    filteredRealisations: Realisation[] = [];

    date: Date;
    maxDateValue: Date;
    disableNext = false;
    allowEdit = true;
    loadingData = true;
    loadingComplete = new EventEmitter<void>();
    lastReload = new Date().getTime();
    highlight?: number;

    userFunctions: string[] = [];
    filteredEmployees = 0;
    totalEmployees = 0;

    fcDate = new FormControl();
    fcHideApproved = new FormControl(false);
    fcUserFunction = new FormControl<string[]>(LocalStorage.userFunctionFilter);
    fcHourType = new FormControl<string[]>(LocalStorage.hourTypeFilter);
    fcExecutor = new FormControl<any>(null);

    executors: User[] = [];
    entities: Entity[] = [];
    selectedRealisations: Realisation[] = [];

    initialExecutorSelectionAllowed = true;
    private daySubscriptions = new Subscription();
    private scheduleSubscription?: Subscription;

    constructor(
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private router: Router,
        private title: Title,
        private userService: UserService,
        private entityService: EntitiesService,
        private realisationService: RealisationService
    ) {
        super();
        this.title.setTitle('Urencontrole' + environment.titleAppend);
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.fcHourType.valueChanges.subscribe(hourtypes => {
                LocalStorage.hourTypeFilter = hourtypes;
                this.applyFilters();
                this.clearSelection();
            })
        );

        this.subscriptions.add(
            this.fcUserFunction.valueChanges.subscribe(functions => {
                LocalStorage.userFunctionFilter = functions;
                this.applyFilters();
                this.clearSelection();
            })
        );

        this.subscriptions.add(
            this.fcHideApproved.valueChanges.subscribe(() => {
                this.applyFilters();
                this.clearSelection();
            })
        );

        this.subscriptions.add(
            this.fcDate.valueChanges.subscribe(value => {
                if (value && Utils.dateString(value as any) !== Utils.dateString(this.date)) {
                    this.router.navigateByUrl(`check-hours/${formatDate(value as any, 'yyyy-MM-dd', 'nl')}`);
                }
            })
        );

        this.subscriptions.add(
            this.fcExecutor.valueChanges.subscribe(() => {
                this.clearSelection();
                this.getData();
            })
        );

        this.subscriptions.add(
            this.userService.userFunctions(false).subscribe(functions => {
                this.userFunctions = functions.data;
            })
        );

        this.subscriptions.add(
            this.activatedRoute.params.subscribe((params: { date: string; realisationId?: number }) => {
                this.daySubscriptions.unsubscribe();
                this.daySubscriptions = new Subscription();

                const today = new Date();
                let paramDate = params.date;

                if (this.maxDate(1) < new Date(params.date)) {
                    paramDate = null;
                }

                if (this.maxDate() < new Date(params.date)) {
                    this.disableNext = true;
                }

                this.date = paramDate ? new Date(paramDate) : new Date(formatDate(today, 'yyyy-MM-dd', 'nl'));
                this.fcDate.patchValue(this.date, { emitEvent: false });

                this.subscriptions.add(
                    this.entityService.getList().subscribe(entities => {
                        this.entities = entities;

                        this.daySubscriptions.add(
                            this.getDayExecutors().subscribe(() => {
                                if (
                                    AuthorisationService.hasFeature(Features.DynamicPerformer) &&
                                    !this.fcExecutor.value &&
                                    this.initialExecutorSelectionAllowed &&
                                    !AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck)
                                ) {
                                    this.fcExecutor.setValue(LocalStorage.user.id, { emitEvent: false });
                                    this.initialExecutorSelectionAllowed = false;
                                }
                                this.getData(params.realisationId ?? null);
                            })
                        );
                    })
                );
            })
        );

        window.onfocus = () => {
            if (new Date().getTime() - this.lastReload > 60000 && !this.loadingData) {
                this.lastReload = new Date().getTime();

                setTimeout(() => this.getData(), 50);
            }
        };
    }

    private getDayExecutors() {
        return this.realisationService.getDayExecutors(this.date).pipe(
            tap(executors => {
                this.executors = executors.data;

                if (
                    !this.executors.find(executor => executor.id === LocalStorage.user?.id) &&
                    AuthorisationService.hasFeature(Features.DynamicPerformer)
                ) {
                    this.executors.push(LocalStorage.user);
                }
            })
        );
    }

    private clearData(): void {
        this.realisations = [];
        this.filteredRealisations = [];
        this.filteredEmployees = 0;
        this.totalEmployees = 0;
    }

    private getData(scrollTo?: number): void {
        this.scheduleSubscription?.unsubscribe();

        const selectedRealisationIds = new Set(
            this.selectedRealisations.map(realisation => realisation.id).filter((id): id is number => id != null)
        );

        this.loadingData = true;

        this.daySubscriptions.add(
            this.realisationService
                .getPreviousDayOpen(
                    this.date,
                    AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck)
                        ? this.fcExecutor.value
                        : LocalStorage.user?.id
                )
                .subscribe(response => {
                    this.openPreviousDays = response.data.sort((a, b) => {
                        return Utils.getTimeOrNull(a.bookdate) - Utils.getTimeOrNull(b.bookdate);
                    });
                })
        );

        this.scheduleSubscription = this.realisationService.getDaySchedule(this.date, this.fcExecutor.value).subscribe({
            next: response => {
                this.realisations = response.data;
                this.selectedRealisations = this.realisations.filter(
                    realisation => realisation.id && selectedRealisationIds.has(realisation.id)
                );

                this.applyFilters();
                this.loadingData = false;
                this.loadingComplete.emit();

                if (scrollTo) {
                    this.scrollToRealisation(scrollTo);
                }
            },
            error: () => {
                this.loadingData = false;
                this.loadingComplete.emit();
            },
        });
        this.daySubscriptions.add(this.scheduleSubscription);
    }

    private applyFilters(): void {
        const visibleRealisations = this.applyEntityAccessFilter(this.realisations ?? []);

        this.filteredRealisations = Utils.filterRealisations(
            visibleRealisations,
            this.fcUserFunction.value,
            this.fcHourType.value,
            this.fcHideApproved.value
        );

        this.totalEmployees = visibleRealisations.filter(realisation => !!realisation.user_id).length;
        this.filteredEmployees = this.filteredRealisations.filter(realisation => !!realisation.user_id).length;
    }

    private applyEntityAccessFilter(realisations: Realisation[]): Realisation[] {
        const belongsToEntityTypes = this.entities.filter(entity => {
            return entity.default_employees?.includes(LocalStorage.user.id);
        });

        const entityTypeIds = belongsToEntityTypes
            .map(entity => entity.entitytypes.map(entityType => entityType.key))
            .flat();

        if (entityTypeIds.length === 0) {
            return realisations;
        }

        return realisations.filter(realisation => {
            if (!realisation.entity_id || !realisation.entity?.entitytypes?.length) {
                return true;
            }

            return realisation.entity.entitytypes.some(entityType => {
                return !entityType.belongs_to || entityTypeIds.includes(String(entityType.belongs_to));
            });
        });
    }

    private scrollToRealisation(realisationId: number): void {
        this.highlight = realisationId;

        setTimeout(() => {
            document.querySelector(`#realisation-${realisationId}`)?.scrollIntoView();
            document.querySelector('#main-container')?.scrollBy(0, -80);
        }, 1000);

        setTimeout(() => {
            this.highlight = null;
        }, 2500);
    }

    private maxDate(add?: number): Date {
        const maxDate = Utils.setTime(new Date(), 0, 0);

        maxDate.setDate(maxDate.getDate() + (7 - maxDate.getDay()));

        const nextWeekFromDay = 4;

        if (
            new Date().getDay() > nextWeekFromDay ||
            (new Date().getDay() === nextWeekFromDay && new Date().getHours() > 11)
        ) {
            maxDate.setDate(maxDate.getDate() + 7);
        }

        if (add) {
            maxDate.setDate(maxDate.getDate() + add);
        } else {
            this.maxDateValue = maxDate;
        }

        return maxDate;
    }

    next(): void {
        const maxDate = this.maxDate();

        if (this.date < maxDate) {
            this.date.setDate(this.date.getDate() + 1);
            this.clearData();
            this.router.navigateByUrl(`check-hours/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}`);
        } else {
            this.disableNext = true;
        }
    }

    prev(): void {
        this.disableNext = false;
        this.date.setDate(this.date.getDate() - 1);
        this.clearData();
        this.router.navigateByUrl(`check-hours/${formatDate(this.date, 'yyyy-MM-dd', 'nl')}`);
    }

    add(): void {
        if (!this.canApproveHours) {
            return;
        }

        const ref = this.dialog.open(AddRealisationDialogComponent, {
            maxWidth: '550px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.realisations,
                bookdate: this.date,
            },
        });

        this.daySubscriptions.add(
            ref.afterClosed().subscribe(realisation => {
                if (realisation) {
                    this.getData(realisation.id);
                }
            })
        );
    }

    editProject(realisation: Realisation): void {
        if (new NotEditablePipe().transform(realisation)) {
            return;
        }

        const ref = this.dialog.open(EditRealisationProjectDialogComponent, {
            maxWidth: '450px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.realisations,
                realisation,
                bookdate: this.date,
            },
        });

        this.daySubscriptions.add(
            ref.afterClosed().subscribe(result => {
                if (result) {
                    this.getData(realisation.id);
                }
            })
        );
    }

    getFromPrevDay(group: RealisationGroup): void {
        const ref = this.dialog.open(CopyRealisationFromOtherDayComponent, {
            data: {
                project: group.project,
                date: this.date,
                realisations: group.realisations ?? [],
            },
            maxWidth: '450px',
        });

        this.daySubscriptions.add(
            ref.afterClosed().subscribe(realisation => {
                if (realisation) {
                    this.getData(realisation.id);
                }
            })
        );
    }

    getStaffFromPrevDay(group: RealisationGroup): void {
        const ref = this.dialog.open(CopyStaffFromOtherDayComponent, {
            data: {
                project: group.project,
                date: this.date,
                realisations: group.realisations ?? [],
            },
            width: '450px',
        });

        this.daySubscriptions.add(
            ref.afterClosed().subscribe(realisation => {
                if (realisation) {
                    this.getData(realisation.id);
                }
            })
        );
    }

    openProjectEditDialog(project: Project): void {
        const dialogRef = this.dialog.open(ProjectEditDialogComponent, {
            width: '800px',
            maxWidth: '90vw',
            data: { projectId: project.afas_project_id },
        });

        this.subscriptions.add(dialogRef.afterClosed().subscribe());
    }

    editProjectComment(project: Project): void {
        this.router.navigate([
            'project-dagrapport',
            project.afas_project_id,
            formatDate(this.date, 'yyyy-MM-dd', 'nl'),
        ]);
    }

    editProjects(realisations: Realisation[]): void {
        if (!this.canApproveHours) {
            return;
        }

        const ref = this.dialog.open(SelectProjectDialogComponent, {
            width: '400px',
            data: {
                currentProjectIds: [],
                errorMessage: `Let op: je wijzigt het project voor ${realisations.length} regels`,
            },
        });

        this.subscriptions.add(
            ref.afterClosed().subscribe(result => {
                if (!result?.project) {
                    return;
                }

                const obs = realisations.map(realisation => {
                    realisation.afas_project_id = result.project.afas_project_id;
                    return this.realisationService.saveRealisation(realisation);
                });

                this.subscriptions.add(
                    forkJoin(obs)
                        .pipe(
                            map((responses: ApiResponse<{ realisation: Realisation }>[]) => {
                                return responses.map(response => response.data.realisation);
                            })
                        )
                        .subscribe({
                            next: realisations => {
                                this.getData(realisations[0].id);
                            },
                        })
                );
            })
        );
    }

    moveSelectionToPerformer(): void {
        if (!AuthorisationService.hasFeature(Features.RealisationScheduleMovePerformerSave)) {
            return;
        }

        const ref = this.dialog.open(MoveRealisationsToPerformerDialogComponent, {
            maxWidth: '450px',
            width: '100vw',
            maxHeight: '100%',
            disableClose: false,
            panelClass: 'comment-edit-dialog',
            data: {
                realisations: this.selectedRealisations,
            },
        });

        this.daySubscriptions.add(
            ref.afterClosed().subscribe(result => {
                if (!result) {
                    return;
                }

                this.getData();
                this.daySubscriptions.add(this.getDayExecutors().subscribe());
                this.clearSelection();
            })
        );
    }

    clearSelection(): void {
        this.selectedRealisations = [];
    }

    get selectionCount(): number {
        return this.selectedRealisations.filter(
            realisation => realisation.user_id && realisation.hourtype === RealisationHourtype.worktime
        ).length;
    }
}

export interface PlanningRealisations {
    planning: Planning;
    project: Project;
    mainPlanningRealisation: Realisation;
    userRealisations: Realisation[];
    entityRealisations: Realisation[];
    entityHiringRealisations: Realisation[];
    hiredStaffRealisations: Realisation[];
    showHiredMaterial: boolean;
    showMaterial: boolean;
    showStaff: boolean;
    showHiredStaff: boolean;
}
